// languagetools:disable

/**
 *--------------- AUTHENTICATION EVENTS ---------------
 */
export const ANALYTICS_EVENTS = {
  auth: {
    /**
     * Event triggered when a user enters their email or phone number to begin signup or login
     * Properties: { from_page: string }
     */
    emailPhoneSubmit: 'auth:email_phone_submit',

    /**
     * Event triggered when verification code is entered
     * Properties: { from_page: string }
     */
    verificationCodeSubmit: 'auth:verification_code_submit',

    /**
     * Event triggered when a user enters their name during the onboarding process
     * Properties: { from_page: string }
     */
    userNameSubmit: 'auth:user_name_submit'
  },

  /**
   *--------------- OFFER FLOW EVENTS ---------------
   */
  offerFlow: {
    /**
     * Event triggered when a user enters the offer flow (PostChatBot mounts)
     * Properties: { postID: string, workspaceID: string | null }
     */
    flowStart: 'offer_flow:flow_start',

    /**
     * Event triggered when a user submits an offer amount
     * Properties: { amount: string }
     */
    amountSubmit: 'offer_flow:amount_submit',

    /**
     * Event triggered when a user initiates login during the offer flow
     */
    loginStart: 'offer_flow:login_start',

    /**
     * Event triggered when a user enters the verification code during the offer flow
     */
    verificationCodeSubmit: 'offer_flow:verification_code_submit',

    /**
     * Event triggered when verification code is successfully verified during the offer flow
     */
    verificationSuccess: 'offer_flow:verification_success',

    /**
     * Event triggered when verification code fails during the offer flow
     */
    verificationFail: 'offer_flow:verification_fail',

    /**
     * Event triggered when a user submits additional offer details (location, empty time)
     * Properties: { hasLocation: boolean, hasEmptyTime: boolean }
     */
    additionalDetailsSubmit: 'offer_flow:additional_details_submit',

    /**
     * Event triggered when a user enters a DOT or MC number during the offer flow
     */
    dotOrMcSubmit: 'offer_flow:dot_or_mc_submit',

    /**
     * Event triggered when a user retries entering a DOT/MC number after a failed lookup
     * Properties: { retryCount: number, dotOrMcType: string, dotOrMcValue: string }
     */
    dotOrMcRetry: 'offer_flow:dot_or_mc_retry',

    /**
     * Event triggered when a user confirms their company during the offer flow
     */
    companyConfirm: 'offer_flow:company_confirm',

    /**
     * Event triggered when a user declines the company match ("No" on company confirmation)
     */
    companyConfirmDecline: 'offer_flow:company_confirm_decline',

    /**
     * Event triggered after company lookup by DOT/MC number completes
     * Properties: { companyFound: boolean, dotOrMcType: string, dotOrMcValue: string }
     */
    companyLookupComplete: 'offer_flow:company_lookup_complete',

    /**
     * Event triggered after workspace lookup by DOT/MC number completes
     * Properties: { workspaceFound: boolean, userIsMember: boolean, dotOrMcType: string, dotOrMcValue: string }
     */
    workspaceLookupComplete: 'offer_flow:workspace_lookup_complete',

    /**
     * Event triggered when workspace verification is initiated after a user confirms their company
     */
    workspaceVerificationStart: 'offer_flow:workspace_verification_start',

    /**
     * Event triggered when a user clicks "Skip for now" to bypass workspace verification
     */
    verificationSkip: 'offer_flow:verification_skip',

    /**
     * Event triggered when the offer mutation completes successfully
     * Properties: { postID: string }
     */
    offerComplete: 'offer_flow:offer_complete',

    /**
     * Event triggered when a new or inactive user begins the onboarding process during the offer flow
     */
    onboardingStart: 'offer_flow:onboarding_start',

    /**
     * Event triggered when a user completes the onboarding process during the offer flow
     */
    onboardingComplete: 'offer_flow:onboarding_complete',

    /**
     * Event triggered when an existing workspace member submits an offer directly (skipping DOT/MC entry)
     * Properties: { workspaceID: string, postID: string }
     */
    existingMemberSubmit: 'offer_flow:existing_member_submit',

    /**
     * Event triggered when a user who entered a DOT/MC number is already a member of the matched workspace
     * Properties: { workspaceID: string, postID: string }
     */
    workspaceMemberSubmit: 'offer_flow:workspace_member_submit',

    /**
     * Event triggered when a user leaves the offer flow before reaching message_after_offer_review
     * Properties: { last_step: string, post_id: string, workspace_id: string | null }
     */
    flowAbandon: 'offer_flow:flow_abandon'
  },

  /**
   *--------------- CHAT EVENTS ---------------
   */
  chat: {
    /**
     * Event triggered when a user sends a message in a chat channel
     * Properties: { hasAttachment: boolean, hasMentions: boolean }
     */
    messageSend: 'chat:message_send'
  },

  /**
   *--------------- LOADBOARD EVENTS ---------------
   */
  loadboard: {
    /**
     * Event triggered when a user clicks on the view loadboard button and modal opens
     */
    modalView: 'loadboard:modal_view'
  },

  /**
   *--------------- SHIPMENT EVENTS ---------------
   */
  shipment: {
    /**
     * Event triggered when a new shipment is created
     */
    create: 'shipment:create',

    /**
     * Event triggered when a user copies a tracking link
     */
    trackingLinkCopy: 'shipment:tracking_link_copy'
  },

  /**
   *--------------- POST EVENTS ---------------
   */
  post: {
    /**
     * Event triggered when an offer is accepted
     * Properties: { post_id: string, offer_id: string }
     */
    offerAccept: 'post:offer_accept',

    /**
     * Event triggered when an offer is rejected
     * Properties: { post_id: string, offer_id: string }
     */
    offerReject: 'post:offer_reject'
  },

  /**
   *--------------- NETWORK EVENTS ---------------
   */
  network: {
    /**
     * Event triggered when a new carrier connection is added
     */
    connectionAdd: 'network:connection_add'
  },

  /**
   *--------------- SETTINGS EVENTS ---------------
   */
  settings: {
    /**
     * Event triggered when an integration is enabled
     * Properties: { integration_name: string }
     */
    integrationEnable: 'settings:integration_enable',

    /**
     * Event triggered when a team member is invited
     */
    memberInvite: 'settings:member_invite'
  },

  /**
   *--------------- NAVIGATION EVENTS ---------------
   */
  navigation: {
    /**
     * Event triggered when a user switches workspaces
     * Properties: { from_workspace: string, to_workspace: string }
     */
    workspaceSwitch: 'navigation:workspace_switch'
  },

  /**
   *--------------- USAGE EVENTS ---------------
   */
  usage: {
    /**
     * Event triggered once per signed-in user per local day to support DAU/MAU reporting
     * without reintroducing pageview tracking.
     * Properties: { surface: 'app' | 'public' | 'embed' }
     */
    appActive: 'usage:app_active'
  },

  /**
   *--------------- AUTOPILOT EVENTS ---------------
   */
  autopilot: {
    /**
     * Event triggered when an autopilot rule is created
     */
    ruleCreate: 'autopilot:rule_create'
  },

  /**
   *--------------- VOICE BOT EVENTS ---------------
   */
  voiceBot: {
    /**
     * Event triggered when a voice bot configuration is created
     */
    configCreate: 'voice_bot:config_create'
  },

  /**
   *--------------- APP INTEGRATION EVENTS ---------------
   */
  apps: {
    /**
     * Event triggered when an app is enabled
     * Properties: { app_name: string }
     */
    enable: 'apps:enable'
  }
} as const

/**
 *--------------- ANALYTICS PAGE METADATA ---------------
 * Used only as `from_page` metadata for milestone events.
 */
export const ANALYTICS_PAGES = {
  /** The signup or login page */
  signupOrLogin: 'signup_or_login',

  /** The verification code entry page */
  codeVerification: 'code_verification',

  /** The user name onboarding page */
  userName: 'user_name_onboarding',

  /**
   *--------------- CARRIER ONBOARDING ---------------
   */

  /** The carrier login form page */
  carrierLoginForm: 'carrier_login_form',

  /** The carrier login flow page */
  carrierLoginFlow: 'carrier_login_flow'
} as const
